import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext } from "react";
import * as yup from "yup";
import ListSelectField from "components/ui/atoms/formFields/ListSelectField";
import DealSharingFormHeader from "./DealSharingFormHeader";
import { ConstantContext } from "components/context/ConstantProvider";
import { Form, FormContext } from "components/shared/Form";
import { buildGenericModelForm } from "components/shared/GenericModelForm";
import FormButtons from "components/ui/atoms/FormButtons";
import PeopleField from "components/ui/atoms/formFields/PeopleField";
import RHFCheckbox from "components/ui/atoms/formFields/RHFCheckbox";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";
import TagField from "components/ui/atoms/formFields/TagField";
import { useMultipleQuery } from "components/shared/useMultipleQuery";
import {
  useCreateSharing,
  useDeleteSharing,
  useSharing,
  useUpdateSharing,
} from "models/sharings/apiSharing";
import { useAllAddressCountries } from "../address/apiAddress";

const dealSharingValidationSchema = yup.object({
  reference_revenue_min: yup
    .number()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  reference_revenue_max: yup
    .number()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  requested_amount_min: yup
    .number()
    .positive()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  requested_amount_max: yup
    .number()
    .positive()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
});

const getInitialValues = (object) => {
  return {
    id: object?.id || null,
    name: object?.name || "",
    comment: object?.comment || "",
    people_ids: object?.people_ids || [],
    people: object?.people || [],
    is_active:
      object?.is_active == undefined ? true : object?.is_active || false,
    investment_round_required: object?.investment_round_required || false,
    investment_round: object?.investment_round || [],
    reference_revenue_required: object?.reference_revenue_required || false,
    reference_revenue_min: object?.reference_revenue_min || "",
    reference_revenue_max: object?.reference_revenue_max || "",
    requested_amount_required: object?.reference_revenue_required || false,
    requested_amount_min: object?.requested_amount_min || "",
    requested_amount_max: object?.requested_amount_max || "",
    deal_classification_ids: object?.deal_classification_ids || [],
    deal_classification_choices: object?.deal_classification_choices || "ANY",
    company_sectors_ids: object?.company_sectors_ids || [],
    company_sectors_choices: object?.company_sectors_choices || "ANY",
    company_business_models_ids: object?.company_business_models_ids || [],
    company_business_models_choices:
      object?.company_business_models_choices || "ANY",
    company_impact_models_ids: object?.company_impact_models_ids || [],
    company_impact_models_choices:
      object?.company_impact_models_choices || "ANY",
    countries_ids: object?.countries_ids || [],
    profitable: object?.profitable === undefined ? null : object?.profitable,
  };
};

const DealSharingFormFields = () => {
  const { dealInvestmentRounds } = useContext(ConstantContext);
  const theme = useTheme();

  const {
    data: { addressCountries },
    isSuccess,
  } = useMultipleQuery({
    addressCountries: useAllAddressCountries(),
  });

  const { object: sharing, formType, onCancel } = useContext(FormContext);

  if (!isSuccess) return <>Loading</>;

  return (
    <>
      <DealSharingFormHeader
        formType={formType}
        onClose={onCancel}
        sharing={sharing}
      />
      <Stack spacing={2} padding={2} sx={{ paddingTop: 10 }}>
        <Card
          variant="outlined"
          sx={{ backgroundColor: `${theme.palette.secondary.extralight}` }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <RHFTextField name="name" label="Name" fullWidth />
              <Box sx={{ minWidth: "100px", ml: 2, textAlign: "end" }}>
                <RHFCheckbox name="is_active" label="Is active" />
              </Box>
            </Stack>
          </CardContent>
          <CardContent>
            <RHFTextField
              name="comment"
              label="Comment"
              fullWidth
              multiline
              maxRows={8}
            />
          </CardContent>
          <CardContent>
            <PeopleField name="people_ids" label="People" multiple creatable />
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Investment Round</Typography>
              <RHFCheckbox name="investment_round_required" label="Required" />
            </Stack>
          </CardContent>
          <CardContent>
            <ListSelectField
              options={dealInvestmentRounds.map((x) => [x.value, x.label])}
              name="investment_round"
              label="Investment rounds"
              multiple
            />
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Reference Revenue</Typography>
              <RHFCheckbox name="reference_revenue_required" label="Required" />
            </Stack>
          </CardContent>
          <CardContent>
            <RHFTextField
              type="number"
              name="reference_revenue_min"
              label="Min"
              placeholder="min"
            />
            <RHFTextField
              sx={{ mx: 2 }}
              type="number"
              name="reference_revenue_max"
              label="Max"
              placeholder="max"
            />
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Requested amount</Typography>
              <RHFCheckbox name="requested_amount_required" label="Required" />
            </Stack>
          </CardContent>
          <CardContent>
            <RHFTextField
              type="number"
              name="requested_amount_min"
              label="Min"
              placeholder="min"
            />
            <RHFTextField
              sx={{ mx: 2 }}
              type="number"
              name="requested_amount_max"
              label="Max"
              placeholder="max"
            />
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6">Classifications</Typography>
          </CardContent>
          <CardContent sx={{ display: "flex" }}>
            <TagField
              name="deal_classification_ids"
              label="Deal classification"
              tagTypeId="TAG_DEAL_CLASSIFICATION"
              multiple
              sx={{ flexGrow: 1 }}
            />
            <ListSelectField
              name="deal_classification_choices"
              options={[
                ["ALL", "All"],
                ["ANY", "Any"],
              ]}
              label="Deal classification choice"
              sx={{ ml: 2, width: "120px" }}
            />
          </CardContent>
          <CardContent sx={{ display: "flex" }}>
            <TagField
              name="company_sectors_ids"
              label="Company sectors"
              tagTypeId="TAG_SECTOR"
              multiple
              sx={{ flexGrow: 1 }}
            />
            <ListSelectField
              name="company_sectors_choices"
              options={[
                ["ALL", "All"],
                ["ANY", "Any"],
              ]}
              label="Company sector choice"
              sx={{ ml: 2, width: "120px" }}
            />
          </CardContent>
          <CardContent sx={{ display: "flex" }}>
            <TagField
              name="company_business_models_ids"
              label="Company business models"
              tagTypeId="TAG_BUSINESS_MODEL"
              multiple
              sx={{ flexGrow: 1 }}
            />
            <ListSelectField
              name="company_business_models_choices"
              options={[
                ["ALL", "All"],
                ["ANY", "Any"],
              ]}
              label="Company business model choice"
              sx={{ ml: 2, width: "120px" }}
            />
          </CardContent>
          <CardContent sx={{ display: "flex" }}>
            <TagField
              name="company_impact_models_ids"
              label="Company Impact Sectors"
              tagTypeId="TAG_IMPACT_MODEL"
              multiple
              sx={{ flexGrow: 1 }}
            />
            <ListSelectField
              name="company_impact_models_choices"
              options={[
                ["ALL", "All"],
                ["ANY", "Any"],
              ]}
              label="Company Impact choice"
              sx={{ ml: 2, width: "120px" }}
            />
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6">Other Classifications</Typography>
          </CardContent>
          <CardContent>
            <RHFCheckbox name="profitable" label="Profitable" nullable />
          </CardContent>
          <CardContent sx={{ display: "flex" }}>
            <ListSelectField
              options={addressCountries.map((x) => [x.id, x.name])}
              name="countries_ids"
              label="Countries"
              multiple
              sx={{ flexGrow: 1 }}
            />
          </CardContent>
        </Card>
        <Box height={80} />
      </Stack>
      <FormButtons />
    </>
  );
};

const DealSharingForm = (props) => {
  return (
    <Form
      validationSchema={dealSharingValidationSchema}
      prepareInitialValues={getInitialValues}
      {...props}
    >
      <DealSharingFormFields />
    </Form>
  );
};

export const ModelDealSharingForm = buildGenericModelForm({
  form: DealSharingForm,
  useGet: useSharing,
  useCreate: useCreateSharing,
  useUpdate: useUpdateSharing,
  useDelete: useDeleteSharing,
});
